import React, { useState, useEffect, useCallback } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonSearchbar,
  IonList,
  IonItem,
  IonLabel,
  IonButton,
  IonButtons,
  IonBackButton,
  IonSpinner,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent
} from '@ionic/react';
import getApi from '../api';

// Förbättrad useDebounce hook som fungerar bättre på Chrome för Android
function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    // Använd både requestAnimationFrame och setTimeout för mer konsekvent beteende
    const raf = requestAnimationFrame(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    });

    return () => {
      cancelAnimationFrame(raf);
    };
  }, [value, delay]);

  return debouncedValue;
}

interface FortnoxCustomer {
  CustomerNumber: string;
  Name: string;
  OrganisationNumber: string;
  YourReference: string;
}

interface Order {
    DocumentNumber: string;
    OrderDate: string;
    Cancelled: boolean;
}

interface CustomerSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectOrder: (orderId: string) => void;
}

const CustomerSearchModal: React.FC<CustomerSearchModalProps> = ({ isOpen, onClose, onSelectOrder }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState<FortnoxCustomer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<FortnoxCustomer | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState<'customer_search' | 'order_list'>('customer_search');

  // Använd vår egen debounce hook för sökningen
  const debouncedSearchQuery = useDebounce(searchQuery, 800);

  const api = getApi();
  
  // Uppdatera sökningen när användaren skriver
  const handleSearch = useCallback((query: string) => {
    // Använd setTimeout(0) för att undvika event loop blocking i Chrome
    setTimeout(() => {
      setSearchQuery(query);
    }, 0);
  }, []);
  
  // Utför sökningen efter debounce
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    
    if (debouncedSearchQuery.length > 2) {
      setIsLoading(true);
      
      api.get(`/fortnox/customers/?search=${encodeURIComponent(debouncedSearchQuery)}`, {
        signal: controller.signal
      })
        .then(res => {
          if (!isMounted) return;
          
          if (res.data && res.data.Customers) {
            setCustomers(res.data.Customers);
          } else {
            setCustomers([]);
          }
          setIsLoading(false);
        })
        .catch(err => {
          if (!isMounted) return;
          if (err.code === 'ERR_CANCELED') {
            console.log('Request canceled:', err.message);
            return;
          }
          
          console.error(err);
          setIsLoading(false);
          setCustomers([]);
        });
    } else {
      setCustomers([]);
    }
    
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [debouncedSearchQuery]);

  useEffect(() => {
    if (selectedCustomer) {
      setIsLoading(true);
      api.get(`/fortnox/orders/?customernumber=${selectedCustomer.CustomerNumber}&sent=False`)
        .then(res => {
          setOrders(res.data.Orders);
          setIsLoading(false);
        })
        .catch(err => {
          console.error(err);
          setIsLoading(false);
        });
    }
  }, [selectedCustomer]);

  const handleCustomerSelect = (customer: FortnoxCustomer) => {
    setSelectedCustomer(customer);
    setView('order_list');
  };

  const handleOrderSelect = (order: Order) => {
    onSelectOrder(order.DocumentNumber);
    resetStateAndClose();
  };

  const resetStateAndClose = () => {
    // Återställ alla tillstånd
    setSearchQuery('');
    setCustomers([]);
    setSelectedCustomer(null);
    setOrders([]);
    setView('customer_search');
    setIsLoading(false);
    
    // Stäng modalen
    onClose();
  };

  const handleBack = () => {
    setView('customer_search');
    setSelectedCustomer(null);
    setOrders([]);
  };

  return (
  <IonModal isOpen={isOpen} onDidDismiss={() => { setSearchQuery(''); resetStateAndClose(); } } placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
      <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          {view === 'order_list' && (
            <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonButton onClick={handleBack} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Tillbaka</IonButton>
            </IonButtons>
          )}
          <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{view === 'customer_search' ? 'Sök Kund' : `Ordrar för ${selectedCustomer?.Name}`}</IonTitle>
          <IonButtons slot="end" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonButton onClick={resetStateAndClose} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Avbryt</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        {view === 'customer_search' ? (
          <>
            <IonSearchbar
              placeholder="Sök på kundnamn eller nummer"
              value={searchQuery}
              onIonChange={e => handleSearch(e.detail.value || '')}
              debounce={0} // Sätt till 0 för att stänga av Ionic's inbyggda debounce
              animated={true}
              showCancelButton="focus"
              inputMode="text"
              enterkeyhint="search" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}            />
            {isLoading && <div className="ion-text-center"><IonSpinner placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} /></div>}
            <div>
              {customers.map(customer => (
                <IonCard key={customer.CustomerNumber} button onClick={() => handleCustomerSelect(customer)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{customer.Name}</IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <p>Kundnummer: {customer.CustomerNumber}</p>
                  </IonCardContent>
                </IonCard>
              ))}
            </div>
          </>
        ) : (
          <>
            {isLoading && <div className="ion-text-center"><IonSpinner placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} /></div>}
            <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              {orders.length > 0 ? (
                orders.map(order => (
                  !order.Cancelled && (
                    <IonItem key={order.DocumentNumber} button onClick={() => handleOrderSelect(order)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <h2>Order: {order.DocumentNumber}</h2>
                        <p>{order.OrderDate}</p>
                      </IonLabel>
                    </IonItem>
                  )
                ))
              ) : (
                <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonLabel className="ion-text-center" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Inga öppna ordrar hittades för denna kund.</IonLabel>
                </IonItem>
              )}
            </IonList>
          </>
        )}
      </IonContent>
    </IonModal>
  );
};

export default CustomerSearchModal;
